import React from "react";
import { LOGO_URL, PHONE_DISPLAY, EMAIL, INSTAGRAM } from "../lib/constants";

/**
 * One-page A4 brochure for The Reset Sanctuary.
 * View at /brochure. Print to PDF via browser (Cmd/Ctrl+P → Save as PDF).
 * Designed to fit a single A4 (210 × 297 mm).
 */

const Brochure = () => {
    return (
        <main
            data-testid="brochure-page"
            style={{
                backgroundColor: "#0a0a0a",
                minHeight: "100vh",
                padding: "40px 16px 80px",
                color: "#bbb",
            }}
        >
            {/* Print button — hidden when printing */}
            <div
                className="brochure-screen-only"
                style={{
                    maxWidth: 794,
                    margin: "0 auto 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                }}
            >
                <p
                    style={{
                        color: "#888",
                        fontSize: 12,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        margin: 0,
                    }}
                >
                    One-page brochure — A4
                </p>
                <button
                    onClick={() => window.print()}
                    className="btn-gold-solid"
                    data-testid="brochure-print-btn"
                    style={{
                        cursor: "pointer",
                        fontFamily: "Inter, sans-serif",
                    }}
                >
                    Save as PDF / Print
                </button>
            </div>

            {/* A4 page (210 × 297 mm rendered at ~96 DPI = 794 × 1123 px) */}
            <article
                className="brochure-page"
                data-testid="brochure-a4"
                style={{
                    width: 794,
                    minHeight: 1123,
                    maxWidth: "100%",
                    margin: "0 auto",
                    backgroundColor: "#0a0a0a",
                    color: "#bbb",
                    boxShadow:
                        "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,162,39,0.18)",
                    padding: "44px 48px",
                    boxSizing: "border-box",
                    position: "relative",
                    fontFamily: "Inter, sans-serif",
                }}
            >
                {/* Hairline gold border inside the page */}
                <div
                    style={{
                        position: "absolute",
                        inset: 20,
                        border: "1px solid rgba(201,162,39,0.22)",
                        pointerEvents: "none",
                    }}
                />

                {/* HEADER */}
                <header
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 18,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <img
                        src={LOGO_URL}
                        alt="The Reset Sanctuary"
                        style={{ height: 78 }}
                    />
                    <div style={{ textAlign: "right" }}>
                        <p
                            style={{
                                color: "#c9a227",
                                fontSize: 9,
                                letterSpacing: "0.35em",
                                textTransform: "uppercase",
                                margin: 0,
                                fontWeight: 500,
                            }}
                        >
                            Recovery by Tatva K
                        </p>
                        <p
                            style={{
                                color: "#888",
                                fontSize: 9,
                                letterSpacing: "0.2em",
                                textTransform: "uppercase",
                                margin: "4px 0 0",
                            }}
                        >
                            Al Hamra, RAK · Al Furjan, Dubai
                        </p>
                    </div>
                </header>

                {/* HERO */}
                <section
                    style={{
                        marginBottom: 22,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <p
                        style={{
                            color: "#c9a227",
                            fontSize: 9,
                            letterSpacing: "0.35em",
                            textTransform: "uppercase",
                            margin: "0 0 10px",
                            fontWeight: 500,
                        }}
                    >
                        Precision Sound Recovery
                    </p>
                    <h1
                        className="font-serif"
                        style={{
                            color: "#ffffff",
                            fontSize: 38,
                            fontWeight: 400,
                            lineHeight: 1.05,
                            letterSpacing: "-0.02em",
                            margin: "0 0 14px",
                        }}
                    >
                        A sanctuary for the{" "}
                        <span style={{ color: "#c9a227" }}>
                            body and mind.
                        </span>
                    </h1>
                    <p
                        style={{
                            color: "#bbb",
                            fontSize: 12,
                            lineHeight: 1.55,
                            margin: 0,
                            maxWidth: 600,
                        }}
                    >
                        For the parent who hasn't slept. The athlete whose body
                        won't recover. The team running on empty. The corporate
                        that never switches off. The animal that holds tension
                        you feel too.
                    </p>
                </section>

                {/* WHAT HAPPENS */}
                <section
                    style={{
                        marginBottom: 22,
                        position: "relative",
                        zIndex: 1,
                        borderTop: "1px solid rgba(201,162,39,0.2)",
                        paddingTop: 16,
                    }}
                >
                    <h2
                        className="font-serif"
                        style={{
                            color: "#c9a227",
                            fontSize: 14,
                            fontWeight: 400,
                            margin: "0 0 8px",
                        }}
                    >
                        What happens in 20 minutes.
                    </h2>
                    <p
                        style={{
                            color: "#bbb",
                            fontSize: 10.5,
                            lineHeight: 1.55,
                            margin: 0,
                        }}
                    >
                        You lie down fully clothed on a precision-calibrated
                        surface. Layered frequency fields interact with the
                        body's own electrical and mechanical systems. Sound
                        delivered through direct tissue contact, spatial
                        acoustic field, and targeted resonance points — each
                        tuned to a specific therapeutic bandwidth. Every
                        session is composed in real time, not played from a
                        playlist. No crystals. No incense. No belief required.
                        Just physics, applied with precision.
                    </p>
                </section>

                {/* WHO IT'S FOR */}
                <section
                    style={{
                        marginBottom: 22,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <h2
                        className="font-serif"
                        style={{
                            color: "#ffffff",
                            fontSize: 14,
                            fontWeight: 500,
                            margin: "0 0 10px",
                            letterSpacing: "0.02em",
                        }}
                    >
                        Who it's for.
                    </h2>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            gap: "6px 14px",
                        }}
                    >
                        {[
                            "Sleep",
                            "Anxiety",
                            "ADHD",
                            "OCD",
                            "Athletes",
                            "Pain Relief",
                            "Migraine",
                            "Corporate",
                            "Mind",
                            "Parents",
                            "Animals",
                            "Private / HNWI",
                        ].map((item) => (
                            <p
                                key={item}
                                style={{
                                    color: "#c9a227",
                                    fontSize: 9,
                                    letterSpacing: "0.18em",
                                    textTransform: "uppercase",
                                    margin: 0,
                                    fontWeight: 500,
                                }}
                            >
                                · {item}
                            </p>
                        ))}
                    </div>
                </section>

                {/* SESSIONS */}
                <section
                    style={{
                        marginBottom: 22,
                        position: "relative",
                        zIndex: 1,
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 14,
                    }}
                >
                    {[
                        {
                            t: "Al Hamra, RAK",
                            items: [
                                "Demo · 20–30 min",
                                "Single, Group, Kids",
                                "Pet, Horse, Camel",
                                "Corporate",
                            ],
                        },
                        {
                            t: "Al Furjan, Dubai",
                            items: [
                                "Single Session",
                                "Group Session",
                                "Corporate Session",
                                "Private Session",
                            ],
                        },
                        {
                            t: "In-Villa · UAE",
                            items: [
                                "Private HNWI · 90 min",
                                "Corporate Retreat",
                                "Equine Session",
                                "Camel Session",
                            ],
                        },
                    ].map((col) => (
                        <div
                            key={col.t}
                            style={{
                                border: "1px solid rgba(201,162,39,0.22)",
                                padding: "10px 12px",
                            }}
                        >
                            <p
                                style={{
                                    color: "#c9a227",
                                    fontSize: 9,
                                    letterSpacing: "0.22em",
                                    textTransform: "uppercase",
                                    margin: "0 0 8px",
                                    fontWeight: 500,
                                }}
                            >
                                {col.t}
                            </p>
                            {col.items.map((i) => (
                                <p
                                    key={i}
                                    style={{
                                        color: "#fff",
                                        fontSize: 9.5,
                                        margin: "3px 0",
                                    }}
                                >
                                    {i}
                                </p>
                            ))}
                        </div>
                    ))}
                </section>

                {/* PRACTITIONER + PHOTO */}
                <section
                    style={{
                        marginBottom: 22,
                        position: "relative",
                        zIndex: 1,
                        borderTop: "1px solid rgba(201,162,39,0.2)",
                        paddingTop: 16,
                        display: "grid",
                        gridTemplateColumns: "1fr 150px",
                        gap: 18,
                        alignItems: "start",
                    }}
                >
                    <div>
                        <p
                            style={{
                                color: "#c9a227",
                                fontSize: 9,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                margin: "0 0 6px",
                                fontWeight: 500,
                            }}
                        >
                            The Practitioner
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: 22,
                                fontWeight: 400,
                                margin: "0 0 6px",
                                letterSpacing: "-0.01em",
                            }}
                        >
                            Tatva K
                        </h2>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10.5,
                                lineHeight: 1.55,
                                margin: 0,
                            }}
                        >
                            Artist. 150M+ aggregate streams. 182K monthly listeners.
                            Frequency Practitioner. Reiki Grandmaster.
                            Certified by ISTA & IPHM. Berklee College of Music
                            — Music Therapy Masterclass. Featured on BBC Radio
                            1 by Bobby Friction, BBC Asian Network, and Rolling
                            Stones India. Curated alongside Talvin Singh and
                            DJ Spooky.
                        </p>
                    </div>
                    <img
                        src="/brochure-assets/tatva.png"
                        alt="Tatva K"
                        data-testid="brochure-tatva-photo"
                        style={{
                            width: "100%",
                            height: 180,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: "1px solid rgba(201,162,39,0.25)",
                            display: "block",
                        }}
                    />
                </section>

                {/* TESTIMONIAL STRIP */}
                <section
                    style={{
                        marginBottom: 22,
                        position: "relative",
                        zIndex: 1,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 14,
                    }}
                >
                    {[
                        {
                            q: "Three years of insomnia. One session. Slept through the night for the first time.",
                            a: "Sarah, Abu Dhabi",
                        },
                        {
                            q: "I'm a physiotherapist. I've tried every recovery modality. This shanti — I've never felt it before.",
                            a: "Akshay Kumar, Physiotherapist",
                        },
                    ].map((t, i) => (
                        <blockquote
                            key={i}
                            style={{
                                margin: 0,
                                padding: "10px 12px",
                                borderLeft: "1.5px solid #c9a227",
                                fontFamily:
                                    "'Playfair Display', Georgia, serif",
                            }}
                        >
                            <p
                                style={{
                                    color: "#fff",
                                    fontSize: 11,
                                    lineHeight: 1.45,
                                    margin: "0 0 6px",
                                    fontWeight: 400,
                                }}
                            >
                                <span style={{ color: "#c9a227" }}>“</span>
                                {t.q}
                            </p>
                            <p
                                style={{
                                    color: "#c9a227",
                                    fontSize: 9,
                                    letterSpacing: "0.2em",
                                    textTransform: "uppercase",
                                    margin: 0,
                                    fontFamily: "Inter, sans-serif",
                                }}
                            >
                                — {t.a}
                            </p>
                        </blockquote>
                    ))}
                </section>

                {/* CTA + CONTACT */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        borderTop: "1px solid rgba(201,162,39,0.25)",
                        paddingTop: 14,
                        display: "grid",
                        gridTemplateColumns: "1.1fr 1fr",
                        gap: 20,
                        alignItems: "center",
                    }}
                >
                    <div>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: 22,
                                fontWeight: 400,
                                margin: "0 0 6px",
                                letterSpacing: "-0.01em",
                            }}
                        >
                            Reserve your{" "}
                            <span style={{ color: "#c9a227" }}>session.</span>
                        </h2>
                        <p
                            style={{
                                color: "#c9a227",
                                fontSize: 10.5,
                                lineHeight: 1.5,
                                margin: "0 0 4px",
                                fontWeight: 500,
                                letterSpacing: "0.02em",
                            }}
                        >
                            Feel the shift in your energy. Then pay.
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.5,
                                margin: 0,
                            }}
                        >
                            20–30 minutes. No commitment. Same equipment. Same
                            practitioner. See how your body responds.
                        </p>
                    </div>
                    <div
                        style={{
                            textAlign: "right",
                            fontFamily: "Inter, sans-serif",
                        }}
                    >
                        <p
                            style={{
                                color: "#c9a227",
                                fontSize: 9,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                margin: "0 0 6px",
                                fontWeight: 500,
                            }}
                        >
                            Reserve via WhatsApp
                        </p>
                        <p
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 18,
                                fontWeight: 500,
                                margin: "0 0 8px",
                                letterSpacing: "0.02em",
                            }}
                        >
                            {PHONE_DISPLAY}
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 2px",
                            }}
                        >
                            {EMAIL}
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 2px",
                            }}
                        >
                            theresetsanctuary.org
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: 0,
                            }}
                        >
                            Instagram {INSTAGRAM}
                        </p>
                    </div>
                </section>

                {/* DISCLAIMER */}
                <p
                    style={{
                        color: "#666",
                        fontSize: 7.5,
                        lineHeight: 1.5,
                        textAlign: "center",
                        marginTop: 20,
                        marginBottom: 0,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    The Reset Sanctuary offers wellness and recovery sessions
                    through precision sound and frequency work. We do not
                    diagnose, treat, or cure medical conditions. Our services
                    are complementary wellness practices, not medical care.
                    Please consult a licensed healthcare provider for medical
                    concerns. © 2026 Tatva K.
                </p>
            </article>
        </main>
    );
};

export default Brochure;
